/**
 * Mobile navigation drawer.
 *
 * Below the `nav` breakpoint (720px) the header shows a hamburger that opens a
 * right-side slide-in drawer. Vanilla port of the design mock's interaction:
 * the panel slides in, the backdrop fades up, drawer links stagger in, and the
 * body scroll is locked. Closes on the X, a backdrop click, any link tap, the
 * Escape key, or a resize back up to desktop.
 *
 * Hooks (set in views/base.twig): [data-menu-open] (hamburger),
 * [data-menu-panel] (drawer), [data-menu-backdrop], [data-menu-close]
 * (X + backdrop + links), [data-drawer-link] (staggered items).
 */

const DESKTOP_MIN = 720;

/**
 * Initializes the mobile navigation drawer.
 * Sets up open, close, focus, and responsive behavior for the mobile menu.
 */
export function initMobileMenu() {
  const openBtn = document.querySelector('[data-menu-open]');
  const panel = document.querySelector('[data-menu-panel]');
  const backdrop = document.querySelector('[data-menu-backdrop]');

  if (!openBtn || !panel || !backdrop) {
    return;
  }

  const closers = document.querySelectorAll('[data-menu-close]');
  const links = panel.querySelectorAll('[data-drawer-link]');
  const closeBtn = panel.querySelector('[data-menu-close]');
  const FOCUSABLE = 'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])';
  let open = false;
  let lastFocused = null;

  /** Open the drawer: show the backdrop + panel, stagger the links in, lock body scroll, and move focus inside. */
  const openMenu = () => {
    if (open) {
      return;
    }
    open = true;
    lastFocused = document.activeElement;

    backdrop.style.opacity = '1';
    backdrop.style.pointerEvents = 'auto';
    panel.style.transform = 'translateX(0)';
    openBtn.setAttribute('aria-expanded', 'true');
    panel.setAttribute('aria-hidden', 'false');

    links.forEach((link, i) => {
      link.style.transitionDelay = `${120 + i * 70}ms`;
      requestAnimationFrame(() => {
        link.style.opacity = '1';
        link.style.transform = 'translateX(0)';
      });
    });

    document.body.style.overflow = 'hidden';

    // Move focus into the drawer so keyboard users land inside the modal nav.
    const firstFocusable = panel.querySelector(FOCUSABLE);
    if (closeBtn) {
      closeBtn.focus();
    } else if (firstFocusable && typeof firstFocusable.focus === 'function') {
      firstFocusable.focus();
    }
  };

  /** Close the drawer: hide the backdrop + panel, reset the link animations, unlock scroll, and restore focus to the opener. */
  const closeMenu = () => {
    if (!open) {
      return;
    }
    open = false;

    backdrop.style.opacity = '0';
    backdrop.style.pointerEvents = 'none';
    panel.style.transform = 'translateX(100%)';
    openBtn.setAttribute('aria-expanded', 'false');
    panel.setAttribute('aria-hidden', 'true');

    links.forEach((link) => {
      link.style.transitionDelay = '0ms';
      link.style.opacity = '0';
      link.style.transform = 'translateX(28px)';
    });

    document.body.style.overflow = '';

    // Restore focus to whatever opened the drawer (usually the hamburger).
    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    } else {
      openBtn.focus();
    }
  };

  openBtn.addEventListener('click', openMenu);
  closers.forEach((el) => el.addEventListener('click', closeMenu));

  // Keep Tab focus cycling within the open drawer.
  panel.addEventListener('keydown', (event) => {
    if (!open || event.key !== 'Tab') {
      return;
    }
    const focusables = panel.querySelectorAll(FOCUSABLE);
    if (!focusables.length) {
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeMenu();
    }
  });

  window.addEventListener('resize', () => {
    if (window.innerWidth >= DESKTOP_MIN && open) {
      closeMenu();
    }
  });
}
